// context/DialogContext.tsx
import { createContext, useContext, useState, ReactNode } from "react";
import { ConfirmDialog, ConfirmDialogProps } from "@/components/molecules/ConfirmDialog";
import { PromptDialog, PromptDialogProps } from "@/components/molecules/PromptDialog";

type ConfirmOptions = Omit<ConfirmDialogProps, "open" | "onConfirm" | "onCancel">;
type PromptOptions = Omit<PromptDialogProps, "open" | "onConfirm" | "onCancel">;

type DialogContextType = {
  showConfirm: (options: ConfirmOptions) => Promise<boolean>;
  showPrompt: (options: PromptOptions) => Promise<string | null>;
};

const DialogContext = createContext<DialogContextType | undefined>(undefined);

export const DialogProvider = ({ children }: { children: ReactNode }) => {
  // Confirm
  const [confirmProps, setConfirmProps] = useState<ConfirmOptions | null>(null);
  const [confirmResolver, setConfirmResolver] = useState<(val: boolean) => void>(() => () => { });

  // Prompt
  const [promptProps, setPromptProps] = useState<PromptOptions | null>(null);
  const [promptResolver, setPromptResolver] = useState<(val: string | null) => void>(() => () => { });

  // Show confirm
  const showConfirm = (options: ConfirmOptions) => {
    setConfirmProps(options);
    return new Promise<boolean>((resolve) => {
      setConfirmResolver(() => resolve);
    });
  };

  const handleConfirmCancel = () => {
    confirmResolver(false);
    setConfirmProps(null);
  };
  const handleConfirmOk = () => {
    confirmResolver(true);
    setConfirmProps(null);
  };

  // Show prompt
  const showPrompt = (options: PromptOptions) => {
    setPromptProps(options);
    return new Promise<string | null>((resolve) => {
      setPromptResolver(() => resolve);
    });
  };

  const handlePromptCancel = () => {
    promptResolver(null);
    setPromptProps(null);
  };
  const handlePromptOk = (value: string) => {
    promptResolver(value);
    setPromptProps(null);
  };

  return (
    <DialogContext.Provider value={{ showConfirm, showPrompt }}>
      {children}

      {confirmProps && (
        <ConfirmDialog
          open={true}
          {...confirmProps}
          onCancel={handleConfirmCancel}
          onConfirm={handleConfirmOk}
        />
      )}

      {promptProps && (
        <PromptDialog
          open={true}
          {...promptProps}
          onCancel={handlePromptCancel}
          onConfirm={handlePromptOk}
        />
      )}
    </DialogContext.Provider>
  );
};

export const useConfirmDialogs = () => {
  const context = useContext(DialogContext);
  if (!context) throw new Error("useConfirmDialogs must be used within DialogProvider");
  return context.showConfirm;
};

export const usePromptDialogs = () => {
  const context = useContext(DialogContext);
  if (!context) throw new Error("usePromptDialogs must be used within DialogProvider");
  return context.showPrompt;
};
